/**
 * FAQPage
 * /faq
 *
 * Cyber-glass overhaul with expanded FAQs, gold-accent accordion,
 * GlowOrbBackground, and FAQPage schema.
 */

import { SEOHead } from '@/components/seo/SEOHead';
import { SchemaInjector } from '@/components/seo/SchemaInjector';
import { GlowOrbBackground, SectionBadge, SectionDivider } from '@/components/marketing';
import { HelpCircle, ChevronDown, ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { getAuthLink } from '@/lib/links';
import { useSanityPage } from '@/lib/sanity.hooks';

const faqs = [
  {
    category: 'Getting Started',
    items: [
      {
        question: 'Is the roof estimator really free?',
        answer:
          'Yes. The standard estimator uses publicly available satellite imagery and is free for unlimited use. We offer an Enhanced Satellite add-on ($19/mo or $0.10/estimate pay-per-use) for AI-powered measurements on complex rooflines. The free version handles the vast majority of US residential addresses.',
      },
      {
        question: 'Do I need to put a credit card down?',
        answer:
          "No. You can sign up and start using the full platform immediately without a credit card. During beta, everything is free. When we launch commercially, you'll get at least 30 days notice before any charges begin.",
      },
      {
        question: 'How long is the beta free period?',
        answer:
          "There is no fixed end date. When we transition out of beta, every active user will receive at least 30 days notice before any charges begin. You'll have time to choose your plan or cancel — completely on your terms.",
      },
    ],
  },
  {
    category: 'Features & Accuracy',
    items: [
      {
        question: 'How accurate are the measurements?',
        answer:
          'Our algorithms are calibrated against thousands of professional reports. Standard accuracy is within 2-5% for pitch and square footage compared to EagleView. The Enhanced Satellite add-on uses AI-powered analysis for complex rooflines and multi-pitch detection, bringing accuracy closer to professional measurement services.',
      },
      {
        question: 'What integrations are available?',
        answer:
          "Rooflyzer integrates with QuickBooks Online, CompanyCam, EagleView, Beacon Roofing Supply, ABC Supply, and more. We also support custom integrations via open API and webhooks. Bring Your Own VOIP lets you plug in your existing call provider's API keys.",
      },
      {
        question: 'Can I export all my data?',
        answer:
          'Yes. Your data is always exportable in full, at any time, at no extra charge. We believe your business data belongs to you — not us. You can export leads, contacts, estimates, invoices, and job history as CSV or JSON.',
      },
    ],
  },
  {
    category: 'Pricing & Plans',
    items: [
      {
        question: 'What is the Founding Member rate?',
        answer:
          'Any user who signs up during beta and remains active when billing starts receives a permanent 30% discount on their base plan — forever. Pro drops from $79 to $55/user/month. Scale drops from $99 to $69/user/month. This is not a promotional period — it is your price for life.',
      },
      {
        question: 'How does Rooflyzer compare to JobNimbus or AccuLynx?',
        answer:
          "JobNimbus starts at $250/mo and AccuLynx at $200+/mo — and neither includes a built-in roof calculator, call tracking, or SMS marketing. A comparable stack with those competitors runs $374+/mo. One Pro seat is $79/month. Marketing Suite ($59), Transactional Communications ($15), and SmartRoof Satellite Estimator ($19) bring that stack to $172/month. PhoneIQ is usage-based. Plus we have a free tier they don't.",
      },
      {
        question: 'Is there a contract or long-term commitment?',
        answer:
          'Never. All plans are month-to-month. Annual billing is not offered yet. You can upgrade, downgrade, or cancel at any time. Your data is always exportable.',
      },
    ],
  },
];

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://rooflyzer.pro' },
    { '@type': 'ListItem', position: 2, name: 'FAQ', item: 'https://rooflyzer.pro/faq' },
  ],
};

const faqSchemaData = { questions: faqs.flatMap((cat) => cat.items) };

/* ─── Accordion Item ──────────────────────────────────────────────── */
function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className={`overflow-hidden cursor-pointer rounded-2xl transition-all duration-300 border ${
        open
          ? 'bg-slate-800/80 border-slate-700'
          : 'bg-slate-900/40 border-slate-800 hover:border-slate-700'
      }`}
      onClick={() => setOpen(!open)}
    >
      <div className="flex items-center justify-between p-5 gap-4">
        <span className="text-white font-medium text-sm sm:text-base leading-relaxed">
          {question}
        </span>
        <ChevronDown
          className={`size-4 text-slate-300 flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
        />
      </div>
      {open && (
        <div className="px-5 pb-5 text-slate-300 text-sm leading-relaxed border-t border-slate-800 pt-4">
          {answer}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */

export const FAQPage = () => {
  const { data: cmsData } = useSanityPage('faq');

  const pageTitle = cmsData?.seo?.metaTitle || 'Frequently Asked Questions — Rooflyzer OS';
  const pageDescription =
    cmsData?.seo?.metaDescription ||
    'Common questions about Rooflyzer OS: pricing, features, integrations, Founding Member rates, data migration, and how we compare to JobNimbus and AccuLynx.';
  const heroTitle = cmsData?.title || 'Frequently Asked Questions';
  const heroSubtitle =
    cmsData?.description ||
    "Everything you need to know before you start. Can't find an answer? Ask us directly.";

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonicalUrl="/faq"
        keywords="rooflyzer FAQ, roofing software FAQ, rooflyzer questions, roofing CRM questions"
        ogImage={cmsData?.seo?.ogImage?.asset?.url || '/social-card.svg'}
        schema={breadcrumbSchema}
      />
      <SchemaInjector type="faq" data={faqSchemaData} />

      {/* ── Hero ──────────────────────────────────────────────────── */}
      <section className="relative pt-28 pb-16 px-4 sm:px-6 text-center overflow-hidden">
        <GlowOrbBackground
          orbs={[
            { color: 'purple', size: 500, top: '-150px', left: '-100px', opacity: 0.14 },
            { color: 'gold', size: 300, top: '50px', right: '-50px', opacity: 0.1 },
          ]}
        />
        <div className="relative z-10 max-w-3xl mx-auto">
          <SectionBadge icon={HelpCircle} text="FAQ" color="purple" className="mb-5" />
          <h1 className="hero-h1 font-bold text-white mb-4">
            {cmsData?.title ? (
              heroTitle
            ) : (
              <>
                Frequently Asked <span className="text-gradient-gold">Questions</span>
              </>
            )}
          </h1>
          <p className="hero-lead text-white/60">
            {cmsData?.description ? (
              heroSubtitle
            ) : (
              <>
                Everything you need to know before you start. Can&apos;t find an answer?{' '}
                <a href="mailto:support@rooflyzer.pro" className="text-emerald-400 hover:underline">
                  Ask us directly.
                </a>
              </>
            )}
          </p>
        </div>
      </section>

      <SectionDivider className="max-w-3xl mx-auto px-4 mb-16" />

      {/* ── FAQ Categories ────────────────────────────────────────── */}
      <section className="pb-20 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto space-y-14">
          {faqs.map((category) => (
            <div key={category.category}>
              <h2 className="section-h3 font-bold text-white mb-5 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-emerald-500 inline-block" />
                {category.category}
              </h2>
              <div className="space-y-3">
                {category.items.map((item) => (
                  <FaqItem key={item.question} question={item.question} answer={item.answer} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <SectionDivider className="max-w-3xl mx-auto px-4 mb-16" />

      {/* ── CTA ────────────────────────────────────────────────────── */}
      <section className="relative py-16 px-4 text-center overflow-hidden">
        <div className="relative z-10 max-w-2xl mx-auto">
          <div className="bg-slate-900/80 rounded-3xl p-8 border border-slate-800">
            <h2 className="section-h2 font-bold text-white mb-3">Still have questions?</h2>
            <p className="text-white/55 body-lg mb-6">
              Our team responds within 24 hours. Or just start free — no commitment required.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Button
                size="lg"
                className="bg-white text-slate-950 font-semibold hover:bg-slate-100 rounded-lg shadow-sm"
                onClick={() => window.location.assign(getAuthLink('/register'))}
              >
                Start Free
                <ArrowRight className="ml-2 size-4" />
              </Button>
              <Button
                size="lg"
                variant="ghost"
                className="border border-slate-700 bg-slate-800/60 text-slate-200 hover:text-white hover:bg-slate-700/60 rounded-lg"
                onClick={() => window.location.assign('mailto:support@rooflyzer.pro')}
              >
                Email Support
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default FAQPage;
